'use client';

import { useState } from 'react';
import styles from './GafeteFileUpload.module.css';
import Button from '../Common/Button/Button.js';
import UploadIcon from '../icons/UploadIcon.js';
import { useTranslation } from 'react-i18next';
import TrashIcon from '../icons/TrashIcon.js';

function GafeteFileUpload({ onFileSelect }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useTranslation();

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  const MAX_FILES = 5;

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const remainingSlots = MAX_FILES - selectedFiles.length;
    const filesToAdd = fileArray.slice(0, remainingSlots);
    let newFilesAdded = 0;

    filesToAdd.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        setErrorMessage(t('fileUpload.invalidFileType'));
        return;
      }

      const fileExists = selectedFiles.some(
        (f) => f.file.name === file.name && f.file.size === file.size,
      );
      if (fileExists) return;

      const reader = new FileReader();
      const fileId = Date.now() + Math.random().toString(36);
      reader.onload = (e) => {
        const newFile = { file, previewUrl: e.target.result, id: fileId };
        setSelectedFiles((prev) => {
          const updated = [...prev, newFile];
          onFileSelect?.(updated.map((f) => f.file));

          if (updated.length >= MAX_FILES) {
            setErrorMessage(t('fileUpload.maxFilesReached', { max: MAX_FILES }));
          } else {
            setErrorMessage(null);
          }
          return updated;
        });
      };
      reader.readAsDataURL(file);
      newFilesAdded++;
    });

    if (fileArray.length > remainingSlots) {
      setErrorMessage(t('fileUpload.maxFilesLimit', { max: MAX_FILES }));
    } else if (selectedFiles.length + newFilesAdded >= MAX_FILES) {
      setErrorMessage(t('fileUpload.maxFilesReached', { max: MAX_FILES }));
    } else {
      setErrorMessage(null);
    }
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragLeave = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      handleFileSelect(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);
      onFileSelect?.(updated.map((f) => f.file));
      if (currentSlide >= updated.length && updated.length > 0) {
        setCurrentSlide(updated.length - 1);
      } else if (updated.length === 0) {
        setCurrentSlide(0);
      }
      return updated;
    });
    setErrorMessage(null);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % selectedFiles.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + selectedFiles.length) % selectedFiles.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const uploadAreaClass = `${styles.uploadArea}`;
  const buttonsContainerClass = `${styles.buttonsContainer} font-small`;
  const maxReached = selectedFiles.length >= MAX_FILES;

  return (
    <div className={styles.container}>
      <div
        className={uploadAreaClass}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={styles.uploadIcon}>
          <UploadIcon />
        </div>
        <div className='mt-2 semi-bold font-medium'>
          {t('attachYourWorkCredential')}
        </div>

        <input
          type='file'
          id='camera-input'
          accept='.jpg,.jpeg,.png'
          capture='environment'
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        <input
          type='file'
          id='gallery-input'
          accept='.jpg,.jpeg,.png'
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        <div className={buttonsContainerClass}>
          <Button
            type='button'
            size='small'
            text={t('fileUpload.takePhoto')}
            color='#fff'
            backgroundColor='#1d9bf0'
            width='70%'
            onClick={() => document.getElementById('camera-input').click()}
            disabled={maxReached}
          />
          <Button
            type='button'
            size='small'
            text={t('fileUpload.chooseFromGallery')}
            color='#1d9bf0'
            backgroundColor='transparent'
            border='1px solid #1d9bf0'
            width='70%'
            onClick={() => document.getElementById('gallery-input').click()}
            disabled={maxReached}
          />
        </div>

        <div className={styles.progressSection}>
          <div className={styles.progressIndicators}>
            {selectedFiles.map((_, index) => (
              <button
                key={index}
                type='button'
                className={`${styles.progressDot} ${
                  index === currentSlide ? styles.progressDotActive : ''
                }`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </div>

        {errorMessage && (
          <div className={`errorLabel position-relative mt-2 mb-2 ${styles.errorMessage}`}>
            {errorMessage}
          </div>
        )}
      </div>

      {selectedFiles.length > 0 && (
        <div className={styles.sliderSection}>
          <div className={styles.sliderContainer}>
            <div className={styles.mainImageWrapper}>
              <img
                src={selectedFiles[currentSlide].previewUrl || '/placeholder.svg'}
                alt={selectedFiles[currentSlide].file.name}
                className={styles.mainImage}
              />

              <button
                type='button'
                className={styles.removeMainBtn}
                onClick={() => removeFile(selectedFiles[currentSlide].id)}
                title='Eliminar foto'
              >
                <TrashIcon />
              </button>

              {selectedFiles.length > 1 && (
                <>
                  <button
                    type='button'
                    className={`${styles.navBtn} ${styles.navBtnPrev}`}
                    onClick={prevSlide}
                  >
                    ‹
                  </button>
                  <button
                    type='button'
                    className={`${styles.navBtn} ${styles.navBtnNext}`}
                    onClick={nextSlide}
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {selectedFiles.length > 1 && (
              <div className={styles.thumbnailsContainer}>
                {selectedFiles.map((fileItem, index) => (
                  <button
                    key={fileItem.id}
                    type='button'
                    className={`${styles.thumbnail} ${
                      index === currentSlide ? styles.thumbnailActive : ''
                    }`}
                    onClick={() => goToSlide(index)}
                  >
                    <img
                      src={fileItem.previewUrl || '/placeholder.svg'}
                      alt={fileItem.file.name}
                      className={styles.thumbnailImage}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GafeteFileUpload;
